import { Link } from 'react-router-dom';
import { Target, Eye, Heart, Shield, Lightbulb, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const ENGINEER_IMAGE = "https://images.unsplash.com/photo-1688841747582-41097036109d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxjaXZpbCUyMGVuZ2luZWVyJTIwc2FmZXR5JTIwaGVsbWV0JTIwcHJvZmVzc2lvbmFsJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzczNDA4MTk0fDA&ixlib=rb-4.1.0&q=85";

const values = [
  {
    icon: Shield,
    title: 'Trust',
    description: 'Building credibility through verified achievements and transparent processes.',
  },
  {
    icon: Eye,
    title: 'Transparency',
    description: 'Clear, honest communication in all interactions between candidates and organizations.',
  },
  {
    icon: Heart,
    title: 'Integrity',
    description: 'Maintaining the highest ethical standards in skill discovery and verification.',
  },
  {
    icon: Lightbulb,
    title: 'Innovation',
    description: 'Continuously improving how professional capability is communicated and discovered.',
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-meritum-navy via-meritum-teal to-meritum-teal">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center text-white">
            <span className="inline-block px-4 py-2 bg-white/10 border border-white/20 rounded-sm text-meritum-gold font-body text-sm font-medium mb-6">
              About Us
            </span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-none mb-6">
              Progress Made Visible
            </h1>
            <p className="font-body text-lg md:text-xl text-slate-200 leading-relaxed max-w-3xl mx-auto">
              MeritumPraxis is a digital platform designed to revolutionize how emerging capability 
              is discovered and interpreted during the transition from formal learning into professional environments.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <Target className="text-meritum-gold" size={28} />
                  <span className="font-body text-sm font-semibold text-meritum-gold uppercase tracking-wide">
                    Our Mission
                  </span>
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-semibold text-meritum-navy tracking-tight mb-4">
                  Bridging Learning and Opportunity
                </h2>
                <p className="font-body text-base md:text-lg text-slate-600 leading-relaxed">
                  We address inefficiencies in how academic, training, research, and practical 
                  performance are communicated, verified, and utilized beyond traditional CV-based 
                  representations. Our mission is to make professional pathways clearer and more 
                  accessible for everyone.
                </p>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Eye className="text-meritum-teal" size={28} />
                  <span className="font-body text-sm font-semibold text-meritum-teal uppercase tracking-wide">
                    Our Vision
                  </span>
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-semibold text-meritum-navy tracking-tight mb-4">
                  Evidence-Based Skill Discovery
                </h2>
                <p className="font-body text-base md:text-lg text-slate-600 leading-relaxed">
                  We envision a world where skills are discovered based on verified evidence rather 
                  than networks or assumptions. Where every individual has the opportunity to 
                  showcase their true capabilities and be recognized for their achievements.
                </p>
              </div>
            </div>

            <div className="relative">
              <img
                src={ENGINEER_IMAGE}
                alt="Engineer professional"
                className="rounded-lg shadow-xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-meritum-gold p-6 rounded-lg shadow-lg">
                <p className="font-heading text-3xl font-bold text-meritum-navy">2026</p>
                <p className="font-body text-sm text-meritum-navy/80">Founded in South Africa</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Purpose */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-3 py-1 bg-meritum-teal/10 text-meritum-teal font-body text-sm font-medium rounded-sm mb-4">
              Core Purpose
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-meritum-navy tracking-tight mb-8">
              What We Do
            </h2>
            <div className="space-y-6 text-left">
              <div className="bg-white p-8 rounded-lg shadow-sm border-l-4 border-meritum-gold">
                <h3 className="font-heading text-xl font-semibold text-meritum-navy mb-3">
                  For Individuals
                </h3>
                <p className="font-body text-slate-600 leading-relaxed">
                  We help individuals build verified professional profiles that evolve over time. 
                  Track your professional and academic progress, link your portfolios (GitHub, YouTube, 
                  Behance, LinkedIn, Google Drive, ORCID), invite referees to verify your accomplishments, 
                  and become discoverable by organizations seeking skills like yours.
                </p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-sm border-l-4 border-meritum-teal">
                <h3 className="font-heading text-xl font-semibold text-meritum-navy mb-3">
                  For Organizations
                </h3>
                <p className="font-body text-slate-600 leading-relaxed">
                  We enable organizations to discover individuals based on evidence rather than 
                  networks. Explore structured, verified performance profiles, identify skills 
                  using comprehensive filters including country search, and move beyond static CVs 
                  to find candidates whose demonstrated growth aligns with your needs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 bg-meritum-gold/10 text-meritum-gold font-body text-sm font-medium rounded-sm mb-4">
              Our Values
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-meritum-navy tracking-tight mb-6">
              What We Stand For
            </h2>
            <p className="font-body text-base md:text-lg text-slate-600 leading-relaxed">
              Our core values guide everything we do at MeritumPraxis.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="text-center p-8 bg-slate-50 rounded-lg hover:bg-meritum-teal/5 transition-colors duration-300 group"
              >
                <div className="w-16 h-16 bg-meritum-gold/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-meritum-gold/20 transition-colors duration-300">
                  <value.icon className="text-meritum-gold" size={28} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-meritum-navy mb-3">
                  {value.title}
                </h3>
                <p className="font-body text-sm text-slate-600 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-meritum-navy">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight mb-6">
            Join the Future of Skill Discovery
          </h2>
          <p className="font-body text-base md:text-lg text-slate-300 leading-relaxed mb-8 max-w-2xl mx-auto">
            Whether you're a candidate looking to showcase your achievements or an organization 
            seeking evidence-based skills, MeritumPraxis is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/pricing">
              <Button className="bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold px-8 py-6 text-base">
                Get Started
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-meritum-navy font-body font-medium px-8 py-6 text-base"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
